import { useEffect, useState } from 'react'

export type Countdown = {
  days: number
  hours: number
  minutes: number
  /** True the moment `targetMs` is reached, and for every instant after it. */
  isExpired: boolean
}

const MS_PER_MINUTE = 60_000
const MS_PER_HOUR = 3_600_000
const MS_PER_DAY = 86_400_000

/**
 * Ticks once a second even though only whole minutes are shown, because the
 * rollover has to land on the second it happens: at a 60s cadence the tiles
 * would sit up to a minute behind, and a viewer watching the last minute run out
 * would see "01 Mins" for two minutes and then jump straight past zero.
 */
const TICK_MS = 1000

export const readCountdown = (targetMs: number, nowMs: number): Countdown => {
  const remaining = targetMs - nowMs

  if (remaining <= 0) {
    return { days: 0, hours: 0, minutes: 0, isExpired: true }
  }

  return {
    days: Math.floor(remaining / MS_PER_DAY),
    hours: Math.floor((remaining % MS_PER_DAY) / MS_PER_HOUR),
    minutes: Math.floor((remaining % MS_PER_HOUR) / MS_PER_MINUTE),
    isExpired: false,
  }
}

/** What an announcement with no deadline reads as: nothing left to count. */
const NO_COUNTDOWN: Countdown = { days: 0, hours: 0, minutes: 0, isExpired: true }

/**
 * The remaining days, hours and minutes until `targetMs`, recomputed every second.
 * `undefined` — a slide that authored no deadline — reads as expired and starts no
 * timer.
 *
 * Seconds are computed away rather than shown: a deadline weeks out with a
 * seconds tile reads as a stopwatch, and the design the tiles come from has
 * three of them. `readCountdown` is exported so a caller that already knows the
 * time — the announcement selector, deciding whether a slide still belongs on the
 * carousel — can ask the same question without starting a second interval.
 *
 * Called by `DashboardSlide` rather than by the tiles, because the slide's own
 * layout turns on whether the tiles are showing: two clocks could disagree for a
 * frame and collapse the layout out from under a countdown still on screen.
 */
export const useCountdown = (targetMs: number | undefined): Countdown => {
  // The CLOCK is the state, not the countdown derived from it. Holding the
  // derived value instead would mean writing state whenever `targetMs` changed,
  // which is a render the component can do for itself — and it would restart the
  // interval on every deadline edit, so the admin preview's tiles would stutter
  // as the operator typed.
  const [nowMs, setNowMs] = useState(() => Date.now())
  const countdown = targetMs === undefined ? NO_COUNTDOWN : readCountdown(targetMs, nowMs)
  // The dependency is whether a timer is WANTED, never the deadline itself, so
  // editing the deadline in the admin preview does not restart the interval. It
  // stops at the deadline as well as starting at one: a `hide_countdown` slide
  // outlives its own countdown, and a timer left running there would re-render
  // the dashboard's highest-traffic surface every second, forever, to show
  // nothing.
  const shouldTick = targetMs !== undefined && !countdown.isExpired

  useEffect(
    function tickTheClock() {
      if (!shouldTick) {
        return
      }

      const id = setInterval(() => {
        setNowMs(Date.now())
      }, TICK_MS)

      return () => {
        clearInterval(id)
      }
    },
    [shouldTick],
  )

  return countdown
}
